import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Clock, Users, FileText, Shield, Globe, ChevronRight, Scan, ChevronDown, ScanFace, KeyRound, QrCode } from "lucide-react";
import { useLogo, LANGUAGES, Flag, TRANSLATIONS, API } from "../App";
import axios from "axios";
import { PublicFooter } from "../components/PublicFooter";

const LandingPage = () => {
  const navigate = useNavigate();
  const { logoUrl } = useLogo();
  const [selectedLang, setSelectedLang] = useState("ja");
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef(null);
  const [navPages, setNavPages] = useState([]);

  // Get translations for current language
  const t = TRANSLATIONS[selectedLang] || TRANSLATIONS.ja;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // コーポレートサイトのナビ用ページを取得
  useEffect(() => {
    axios
      .get(`${API}/site/pages`)
      .then((res) => setNavPages((res.data || []).filter((p) => p.show_in_nav)))
      .catch(() => {});
  }, []);

  const features = [
    {
      icon: <QrCode className="w-6 h-6" />,
      title: { ja: "QRコード認証", en: "QR Code Auth", pt: "Autenticação QR", vi: "Xác Thực QR", tl: "QR Code Auth", es: "Autenticación QR" },
      desc: { ja: "従業員証のQRコードで簡単打刻", en: "Easy clock in/out with employee QR code", pt: "Registro fácil com código QR", vi: "Chấm công dễ dàng bằng mã QR", tl: "Easy clock in/out with QR", es: "Registro fácil con código QR" }
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: { ja: "多言語対応", en: "Multi-language", pt: "Multi-idioma", vi: "Đa Ngôn Ngữ", tl: "Multi-language", es: "Multilingüe" },
      desc: { ja: "6言語での母国語表示", en: "Native language display in 6 languages", pt: "Exibição em idioma nativo em 6 idiomas", vi: "Hiển thị 6 ngôn ngữ", tl: "6 na wika", es: "Visualización en 6 idiomas" }
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: { ja: "15分刻み計算", en: "15-min Rounding", pt: "Arredondamento 15min", vi: "Làm Tròn 15 Phút", tl: "15-min Rounding", es: "Redondeo 15min" },
      desc: { ja: "出勤切捨て・退勤切上げの自動計算", en: "Auto round-down clock-in, round-up clock-out", pt: "Arredondamento automático de entrada/saída", vi: "Tự động làm tròn vào/ra", tl: "Auto round-down/up", es: "Redondeo automático entrada/salida" }
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: { ja: "CSV連携", en: "CSV Export", pt: "Exportação CSV", vi: "Xuất CSV", tl: "CSV Export", es: "Exportación CSV" },
      desc: { ja: "給与システムへのデータ連携", en: "Data export to payroll systems", pt: "Exportação de dados para folha de pagamento", vi: "Xuất dữ liệu sang hệ thống lương", tl: "Data export to payroll", es: "Exportación a nóminas" }
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 dark:bg-slate-950/80 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={logoUrl} alt="PRC勤怠" className="w-10 h-10 object-contain rounded-xl" />
              <span className="font-bold text-xl tracking-tight">PRC勤怠</span>
            </div>
            <nav className="hidden lg:flex items-center gap-6">
              {navPages.map((p) => (
                <Link
                  key={p.slug}
                  to={`/p/${p.slug}`}
                  className="text-sm text-slate-600 dark:text-slate-300 hover:text-emerald-500 transition-colors whitespace-nowrap"
                  data-testid={`landing-nav-${p.slug}`}
                >
                  {p.nav_label}
                </Link>
              ))}
              <Link
                to="/guide"
                className="text-sm text-slate-600 dark:text-slate-300 hover:text-emerald-500 transition-colors whitespace-nowrap"
                data-testid="nav-link-guide"
              >
                取扱説明書
              </Link>
              <Link
                to="/estimate"
                className="text-sm text-slate-600 dark:text-slate-300 hover:text-emerald-500 transition-colors whitespace-nowrap"
                data-testid="nav-link-estimate"
              >
                導入見積
              </Link>
            </nav>
            <div className="flex items-center gap-4">
              {/* Custom Language Dropdown with Flags */}
              <div className="relative" ref={langDropdownRef}>
                <button
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                  className="flex items-center gap-2 text-sm bg-transparent border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  data-testid="language-selector"
                >
                  <Flag countryCode={LANGUAGES.find(l => l.code === selectedLang)?.countryCode || 'jp'} size={20} />
                  <span>{LANGUAGES.find(l => l.code === selectedLang)?.name}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${langDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {langDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50">
                    {LANGUAGES.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => { setSelectedLang(lang.code); setLangDropdownOpen(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-100 dark:hover:bg-slate-700 first:rounded-t-lg last:rounded-b-lg ${selectedLang === lang.code ? 'bg-slate-100 dark:bg-slate-700' : ''}`}
                      >
                        <Flag countryCode={lang.countryCode} size={20} />
                        <span className="text-sm font-medium">{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Link to="/login">
                <Button variant="outline" className="hidden sm:flex" data-testid="admin-login-btn">
                  {t.adminLogin}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 dark:bg-slate-800 text-sm font-medium">
                <Shield className="w-4 h-4 text-emerald-500" />
                <span>{selectedLang === "ja" ? "AI搭載・なりすまし防止機能" : selectedLang === "en" ? "AI-powered with anti-spoofing" : selectedLang === "pt" ? "IA com anti-spoofing" : selectedLang === "vi" ? "AI chống giả mạo" : selectedLang === "tl" ? "AI with anti-spoofing" : "IA con anti-spoofing"}</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
                {selectedLang === "ja" ? (
                  <>多国籍チームの<span className="block text-emerald-500">勤怠管理を</span>シンプルに</>
                ) : selectedLang === "en" ? (
                  <>Simple<span className="block text-emerald-500">Attendance</span>for Global Teams</>
                ) : selectedLang === "pt" ? (
                  <>Controle de Ponto<span className="block text-emerald-500">Simples</span>para Equipes Globais</>
                ) : selectedLang === "vi" ? (
                  <>Chấm Công<span className="block text-emerald-500">Đơn Giản</span>cho Đội Ngũ Đa Quốc Gia</>
                ) : selectedLang === "tl" ? (
                  <>Simple<span className="block text-emerald-500">Attendance</span>for Global Teams</>
                ) : (
                  <>Control de<span className="block text-emerald-500">Asistencia</span>para Equipos Globales</>
                )}
              </h1>
              <p className="text-lg text-slate-600 dark:text-slate-400 max-w-lg">
                {t.landingDescription}
              </p>
              
              {/* QRコード認証ボタン */}
              <div className="space-y-3">
                <Button 
                  size="lg"
                  className="btn-primary rounded-xl text-lg px-6 py-5 flex items-center gap-3 w-full"
                  onClick={() => navigate("/face-auth?method=qr")}
                  data-testid="start-qr-auth-btn"
                >
                  <QrCode className="w-5 h-5" />
                  {selectedLang === "ja" ? "QRコード認証" : "QR Code Auth"}
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </Button>
              </div>
              
              <div className="pt-4">
                <Button 
                  variant="ghost" 
                  size="lg"
                  className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                  onClick={() => navigate("/login")}
                  data-testid="admin-panel-btn"
                >
                  {t.adminLogin}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square max-w-md mx-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-slate-500/20 rounded-3xl blur-3xl" />
                <div className="relative bg-white dark:bg-slate-900 rounded-3xl shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800">
                  <img
                    src="https://customer-assets-4nw71qhi.emergentagent.net/job_shift-tracker-184/artifacts/ti0dqz5y_%7BB2CC93C1-3864-4F6F-9F50-E5F93013EAD2%7D.png"
                    alt="QR Code Scanning Robot"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-48 h-48 rounded-full border-4 border-emerald-500 pulse-ring" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight mb-4">{t.features}</h2>
            <p className="text-slate-600 dark:text-slate-400">{selectedLang === "ja" ? "効率的な勤怠管理に必要な機能を完備" : selectedLang === "en" ? "All features needed for efficient attendance management" : selectedLang === "pt" ? "Todos os recursos necessários para gestão eficiente" : selectedLang === "vi" ? "Đầy đủ tính năng quản lý chấm công" : selectedLang === "tl" ? "Lahat ng kailangan para sa attendance management" : "Todas las funciones necesarias para la gestión"}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, i) => (
              <div 
                key={i} 
                className="p-6 rounded-2xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:shadow-lg transition-all duration-300"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-slate-900 dark:bg-white flex items-center justify-center text-white dark:text-slate-900 mb-4">
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-lg mb-2">{feature.title[selectedLang] || feature.title.en}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">{feature.desc[selectedLang] || feature.desc.en}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Languages Section */}
      <section className="py-24 bg-slate-50 dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight mb-4">{t.supportedLanguages}</h2>
            <p className="text-slate-600 dark:text-slate-400">{t.multiLanguageDesc}</p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {LANGUAGES.map(lang => (
              <div 
                key={lang.code}
                className="flex items-center gap-3 px-6 py-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm"
                data-testid={`lang-badge-${lang.code}`}
              >
                <Flag countryCode={lang.countryCode} size={32} />
                <span className="font-medium">{lang.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-900 dark:bg-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">{t.getStarted}</h2>
          <p className="text-slate-400 mb-8 text-lg">{selectedLang === "ja" ? "QRコードで打刻するか、管理画面にログインして従業員を登録しましょう" : selectedLang === "en" ? "Clock in with QR code or login to the admin panel" : selectedLang === "pt" ? "Registre ponto com código QR ou acesse o painel admin" : selectedLang === "vi" ? "Chấm công bằng mã QR hoặc đăng nhập quản trị" : selectedLang === "tl" ? "Mag-clock in gamit ang QR code o mag-login sa admin panel" : "Fichar con código QR o acceder al panel de administración"}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-xl text-lg px-8 py-6"
              onClick={() => navigate("/face-auth?method=qr")}
              data-testid="cta-qr-auth-btn"
            >
              {selectedLang === "ja" ? "QRコード認証" : "QR Code Auth"}
            </Button>
            <Button 
              size="lg"
              variant="outline" 
              className="border-white text-white hover:bg-white/10 rounded-xl text-lg px-8 py-6"
              onClick={() => navigate("/login")}
              data-testid="cta-login-btn"
            >
              {t.adminLogin}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
};

export default LandingPage;
